import { CollapsibleFilters } from '../common/CollapsibleFilters';
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Table,
  Typography,
  Button,
  Select,
  Option,
  FormControl,
  FormLabel,
  Input,
  Chip,
  IconButton,
  LinearProgress,
  Alert,
  Stack,
} from '@mui/joy';
import { MagnifyingGlass as SearchIcon, Plus as AddIcon, PencilSimple as EditIcon, Trash as DeleteIcon } from '@phosphor-icons/react';
import { priceListApi, distributorsApi, type PriceListItem, type Distributor } from '../../lib/distributorsApi';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';

interface Props {
  onEdit?: (item: PriceListItem) => void;
  onAdd?: () => void;
  refreshTrigger?: number;
}

// Product categories from backend
const PRODUCT_CATEGORIES = [
  { value: 'shingles', label: 'Shingles' },
  { value: 'underlayment', label: 'Underlayment' },
  { value: 'ridge_vents', label: 'Ridge Vents' },
  { value: 'flashing', label: 'Flashing' },
  { value: 'fasteners', label: 'Fasteners' },
  { value: 'adhesives', label: 'Adhesives' },
  { value: 'gutters', label: 'Gutters' },
  { value: 'insulation', label: 'Insulation' },
  { value: 'ventilation', label: 'Ventilation' },
  { value: 'tools', label: 'Tools' },
  { value: 'safety', label: 'Safety Equipment' },
  { value: 'misc', label: 'Miscellaneous' },
];

export default function ProductsList({ onEdit, onAdd, refreshTrigger }: Props) {
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const [products, setProducts] = useState<PriceListItem[]>([]);
  const [distributors, setDistributors] = useState<Distributor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);
  const pageSize = 50; // Request 50 items per page

  // Filters
  const [distributorFilter, setDistributorFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<string>('all');

  const isAdmin = (user as any)?.role === 'admin' || (user as any)?.is_organization_admin;

  useEffect(() => {
    loadDistributors();
  }, []);

  useEffect(() => {
    // Reset to first page when filters change
    setCurrentPage(1);
    loadProducts(1);
  }, [distributorFilter, categoryFilter, activeFilter, searchQuery, refreshTrigger]);

  useEffect(() => {
    // Load products when page changes (but not when filters change)
    if (currentPage > 1) {
      loadProducts(currentPage);
    }
  }, [currentPage]);

  const loadDistributors = async () => {
    try {
      const response = await distributorsApi.list({ is_active: true });
      setDistributors(response?.results || []);
    } catch {
      // Intentionally empty
    }
  };

  const loadProducts = async (page: number = 1) => {
    try {
      setLoading(true);
      setError(null);

      const params: any = {
        page: page,
        page_size: pageSize,
      };

      if (distributorFilter !== 'all') {
        params.distributor = parseInt(distributorFilter);
      }
      if (categoryFilter !== 'all') {
        params.category = categoryFilter;
      }
      if (activeFilter !== 'all') {
        params.is_active = activeFilter === 'active';
      }
      if (searchQuery) {
        params.search = searchQuery;
      }

      const response = await priceListApi.list(params);
      setProducts(response?.results || []);
      setTotalCount(response?.count || 0);
      setHasNext(!!response?.next);
      setHasPrevious(!!response?.previous);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to load products');
      setProducts([]);
      setTotalCount(0);
      setHasNext(false);
      setHasPrevious(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    // Reset to first page and reload with search
    setCurrentPage(1);
    loadProducts(1);
  };

  const handleDelete = async (item: PriceListItem) => {
    if (!window.confirm(`Are you sure you want to delete "${item.product_name}"?`)) {
      return;
    }

    try {
      await priceListApi.delete(item.id);
      showSuccess('Product deleted successfully');
      loadProducts(currentPage);
    } catch (err: any) {
      showError(err.response?.data?.detail || 'Failed to delete product');
    }
  };

  const formatCurrency = (amount: string | number) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(num);
  };

  const getCategoryLabel = (category: string) => {
    const cat = PRODUCT_CATEGORIES.find(c => c.value === category);
    return cat?.label || category;
  };

  const getDistributorName = (distributorId: number) => {
    const distributor = distributors.find(d => d.id === distributorId);
    return distributor?.name || 'Unknown';
  };

  return (
    <Card>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
          <Typography level="h3">Products</Typography>

          {isAdmin && onAdd && (
            <Button
              startDecorator={<AddIcon />}
              onClick={onAdd}
              color="primary"
            >
              Add Product
            </Button>
          )}
        </Box>

        {/* Filters */}
        <CollapsibleFilters><Stack spacing={2} direction={{ xs: 'column', md: 'row' }} sx={{ mb: 3 }}>
          <FormControl size="sm" sx={{ minWidth: 200 }}>
            <FormLabel>Distributor</FormLabel>
            <Select
              value={distributorFilter}
              onChange={(_, value) => setDistributorFilter(value as string)}
            >
              <Option value="all">All Distributors</Option>
              {distributors.map(dist => (
                <Option key={dist.id} value={dist.id.toString()}>
                  {dist.name}
                </Option>
              ))}
            </Select>
          </FormControl>

          <FormControl size="sm" sx={{ minWidth: 200 }}>
            <FormLabel>Category</FormLabel>
            <Select
              value={categoryFilter}
              onChange={(_, value) => setCategoryFilter(value as string)}
            >
              <Option value="all">All Categories</Option>
              {PRODUCT_CATEGORIES.map(cat => (
                <Option key={cat.value} value={cat.value}>
                  {cat.label}
                </Option>
              ))}
            </Select>
          </FormControl>

          <FormControl size="sm" sx={{ minWidth: 150 }}>
            <FormLabel>Status</FormLabel>
            <Select
              value={activeFilter}
              onChange={(_, value) => setActiveFilter(value as string)}
            >
              <Option value="all">All Status</Option>
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
            </Select>
          </FormControl>

          <FormControl size="sm" sx={{ flex: 1 }}>
            <FormLabel>Search</FormLabel>
            <Input
              placeholder="Search by name or ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              endDecorator={
                <IconButton size="sm" onClick={handleSearch}>
                  <SearchIcon />
                </IconButton>
              }
            />
          </FormControl>
        </Stack></CollapsibleFilters>

        {/* Error Display */}
        {error && (
          <Alert color="danger" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Loading */}
        {loading && <LinearProgress />}

        {/* No Products */}
        {!loading && products.length === 0 && (
          <Alert color="neutral">
            No products found. {isAdmin ? 'Add products manually or use the Upload Prices tab to import from a file.' : 'Contact an administrator to add products.'}
          </Alert>
        )}

        {/* Products Table */}
        {!loading && products.length > 0 && (
          <Box sx={{ overflowX: 'auto' }}>
            <Table>
              <thead>
                <tr>
                  <th>Product Name</th>
                  <th>Product ID</th>
                  <th>Distributor</th>
                  <th>Category</th>
                  <th>Unit Price</th>
                  <th>Unit</th>
                  <th>Status</th>
                  {isAdmin && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {products.map((product) => (
                  <tr key={product.id}>
                    <td>
                      <Typography level="body-md" fontWeight="lg">
                        {product.product_name}
                      </Typography>
                    </td>
                    <td>{product.product_id || '-'}</td>
                    <td>{getDistributorName(product.distributor)}</td>
                    <td>
                      <Chip size="sm" variant="soft">
                        {getCategoryLabel(product.category)}
                      </Chip>
                    </td>
                    <td>{formatCurrency(product.unit_price)}</td>
                    <td>{product.unit_of_measure}</td>
                    <td>
                      <Chip
                        color={product.is_active ? 'success' : 'danger'}
                        size="sm"
                      >
                        {product.is_active ? 'Active' : 'Inactive'}
                      </Chip>
                    </td>
                    {isAdmin && (
                      <td>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {onEdit && (
                            <IconButton
                              size="sm"
                              variant="outlined"
                              onClick={() => onEdit(product)}
                              title="Edit Product"
                            >
                              <EditIcon />
                            </IconButton>
                          )}
                          <IconButton
                            size="sm"
                            variant="outlined"
                            color="danger"
                            onClick={() => handleDelete(product)}
                            title="Delete Product"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </Table>
          </Box>
        )}

        {/* Pagination */}
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography level="body-sm" color="neutral">
            {totalCount > 0
              ? `Showing ${Math.min((currentPage - 1) * pageSize + 1, totalCount)}–${Math.min(currentPage * pageSize, totalCount)} of ${totalCount} products`
              : 'No products'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              color="primary"
              size="sm"
              disabled={!hasPrevious}
              onClick={() => setCurrentPage(prev => prev - 1)}
            >
              Previous
            </Button>
            <Button
              variant="outlined"
              color="primary"
              size="sm"
              disabled={!hasNext}
              onClick={() => setCurrentPage(prev => prev + 1)}
            >
              Next
            </Button>
          </Box>
        </Box>
      </Box>
    </Card>
  );
}
